/** Fluxo TV: `/entregas.mp3` e, depois do som, Web Speech pt-BR. Retorna cleanup. */
export function speakPedidoProntoRetirada(customerName: string): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const textoModelo = `${customerName.trim() || "Cliente"}, vá até o balcão retirar seu pedido!`;

  const audio = new Audio("/entregas.mp3");
  audio.volume = 0.7;

  let cancelado = false;
  let listenerVozes: EventListener | undefined;
  let idFallbackTimer = 0;
  let idRetentativaFala = 0;
  let idEsperaVoz = 0;
  let idAtrasoParaTtsDepoisSom = 0;

  /** Um unico arranque do fluxo TTS apos o alarme (entre `ended`/erro/fallback). */
  let cicloTtsPosSomRegistado = false;
  /** So um `iniciarOuvir` effectivo (vozes + speak + retentativa). */
  let jaMontouSpeakDesteSom = false;
  /** Lista de vozes: no maximo um bloco `voiceschanged` + fallback. */
  let passouPelosGatewaysVozes = false;

  const tirarSynthDoEstadoBugadoChrome = (): void => {
    if (!("speechSynthesis" in window)) return;
    try {
      window.speechSynthesis.resume();
    } catch {
      /* motor sem resume */
    }
  };

  function vozPortuguesOuPadrao(): SpeechSynthesisVoice | undefined {
    void window.speechSynthesis.getVoices();
    const todas = window.speechSynthesis.getVoices();
    return (
      todas.find((w) => w.lang.toLowerCase().replace("_", "-") === "pt-br") ??
      todas.find((w) => w.lang.toLowerCase().startsWith("pt-br")) ??
      todas.find((w) => w.lang.toLowerCase().startsWith("pt")) ??
      todas[0]
    );
  }

  const sintetizarUmaLinha = (forcarEscolhaVozPortuguesa: boolean): void => {
    if (cancelado || !("speechSynthesis" in window)) return;
    tirarSynthDoEstadoBugadoChrome();

    const synth = window.speechSynthesis;
    synth.cancel();
    tirarSynthDoEstadoBugadoChrome();

    window.setTimeout(() => {
      if (cancelado) return;

      const u = new SpeechSynthesisUtterance(textoModelo);
      u.lang = "pt-BR";
      u.rate = 0.88;
      u.pitch = 1.06;
      u.volume = 1;

      if (forcarEscolhaVozPortuguesa) {
        const v = vozPortuguesOuPadrao();
        if (v) {
          try {
            u.voice = v;
          } catch {
            /* voz ilegal para motor */
          }
        }
      }

      synth.resume();
      synth.speak(u);
    }, 115);
  };

  /** Dispara a fala inicial + repeticao só se ficou em silencio absoluto (`pending`/`speaking`). */
  const cicloSpeakDepoisSom = (): void => {
    if (cancelado || cicloTtsPosSomRegistado) return;
    cicloTtsPosSomRegistado = true;

    tirarSynthDoEstadoBugadoChrome();
    const synthInit = window.speechSynthesis;
    void synthInit.getVoices();

    const iniciarOuvir = (): void => {
      if (cancelado || jaMontouSpeakDesteSom) return;
      jaMontouSpeakDesteSom = true;

      sintetizarUmaLinha(true);

      window.clearTimeout(idRetentativaFala);
      idRetentativaFala = window.setTimeout(() => {
        if (cancelado) return;
        const s = window.speechSynthesis;
        /* IMPORTANTE: ignorar apenas `paused` — em Chrome ficava `paused` sem speak e barrava antes. */
        if (s.speaking || s.pending) return;
        tirarSynthDoEstadoBugadoChrome();
        s.cancel();
        sintetizarUmaLinha(false);
      }, 700);
    };

    const numVozes = synthInit.getVoices().length;

    /*
     * Lista vazia e normal no primeiro paint; igualmente tentamos `speak` so com `lang`
     * porque alguns Chromes sintetizam sem expor entrada em `getVoices()` (bugs Linux).
     */
    if (numVozes > 0) {
      iniciarOuvir();
      return;
    }

    if (!passouPelosGatewaysVozes) {
      passouPelosGatewaysVozes = true;

      const onVoicesChanged: EventListener = () => {
        window.clearTimeout(idEsperaVoz);
        idEsperaVoz = 0;
        window.speechSynthesis.removeEventListener(
          "voiceschanged",
          onVoicesChanged
        );
        listenerVozes = undefined;
        iniciarOuvir();
      };

      listenerVozes = onVoicesChanged;
      window.speechSynthesis.addEventListener("voiceschanged", onVoicesChanged);
      void window.speechSynthesis.getVoices();

      /* Linux: `voiceschanged` pode falhar — falar na mesma com lang default */
      idEsperaVoz = window.setTimeout(() => {
        window.speechSynthesis.removeEventListener(
          "voiceschanged",
          onVoicesChanged
        );
        listenerVozes = undefined;
        iniciarOuvir();
      }, 820);
      return;
    }

    iniciarOuvir();
  };

  const depoisOuDuranteSom = (): void => {
    window.clearTimeout(idFallbackTimer);
    idFallbackTimer = 0;
    if (cancelado) return;
    window.clearTimeout(idAtrasoParaTtsDepoisSom);
    idAtrasoParaTtsDepoisSom = window.setTimeout(cicloSpeakDepoisSom, 360);
  };

  audio.addEventListener("ended", depoisOuDuranteSom, { once: true });
  audio.addEventListener("error", depoisOuDuranteSom, { once: true });
  /* Se 'ended' nao disparar, ainda assim chama cicloSpeak */
  idFallbackTimer = window.setTimeout(depoisOuDuranteSom, 6200);

  void audio.play().catch(() => {
    window.clearTimeout(idFallbackTimer);
    idFallbackTimer = window.setTimeout(depoisOuDuranteSom, 450);
  });

  return () => {
    cancelado = true;
    cicloTtsPosSomRegistado = false;
    jaMontouSpeakDesteSom = false;
    passouPelosGatewaysVozes = false;

    window.clearTimeout(idFallbackTimer);
    window.clearTimeout(idRetentativaFala);
    window.clearTimeout(idEsperaVoz);
    window.clearTimeout(idAtrasoParaTtsDepoisSom);

    audio.removeEventListener("ended", depoisOuDuranteSom);
    audio.removeEventListener("error", depoisOuDuranteSom);

    audio.pause();
    audio.currentTime = 0;

    if (listenerVozes && "speechSynthesis" in window) {
      window.speechSynthesis.removeEventListener(
        "voiceschanged",
        listenerVozes
      );
      listenerVozes = undefined;
    }

    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  };
}
